import styled, { css } from 'styled-components';

import { Colors, Theme } from '../../types';

import { getBackgroundColorType, getBorderColorType, IStyledImageBox, IStyledOption } from '../index';
import { Box } from '../../Box';

const getBackgroundColor = (data: getBackgroundColorType): Colors => {
    const { checked, backgroundColor, checkedBackgroundColor } = data;

    const defaultColors: Colors = checked ? 'primary-default' : 'surface-default';

    if (backgroundColor && checkedBackgroundColor) {
        const customColors: Colors = checked ? checkedBackgroundColor : backgroundColor;

        return customColors;
    }

    return defaultColors;
};

const getBorderColor = (data: getBorderColorType): Colors => {
    const { borderColor, checkedBorderColor, checked } = data;

    const defaultColors: Colors = checked ? 'primary-default' : 'border-default';

    if (borderColor && checkedBorderColor) {
        return checked ? checkedBorderColor : borderColor;
    }

    return defaultColors;
};

export const StyledOptionBox = styled.div<IStyledOption>`
    position: relative;
    ${({ mb }) => mb && `margin-bottom: ${mb}px;`};
    width: ${({ short }) => short && 'calc(50% - 6px)'};
`;

export const StyledOption = styled.div<IStyledOption>`
    position: relative;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    border-radius: 12px;
    min-height: 80px;
    height: ${({ isLarge, short }) => {
        if (isLarge) {
            return '96px';
        } else {
            return short ? '80px' : 'auto';
        }
    }};

    background-color: ${({ theme, checked, backgroundColor, checkedBackgroundColor }) =>
        theme[getBackgroundColor({ checked, backgroundColor, checkedBackgroundColor })]};

    border: 2px solid transparent;
    border-radius: 12px;

    ${({ checked, indicated }) =>
        indicated &&
        css`
            border-color: ${() =>
                checked ? '#FCC419' : '#FFFFFF'};
            background-color: ${({ theme }) => theme['surface-default']};
            transition: border-color 0.3s;
        `};

    // for custom border settings
    border-color: ${({ theme, borderColor, checkedBorderColor, checked }) =>
        borderColor && checkedBorderColor && theme[getBorderColor({ borderColor, checkedBorderColor, checked })]};

    // padding settings
    padding-top: 14px;
    padding-bottom: 14px;
    padding-left: ${({ theme, leftImage, leftIcon, leftCheckbox, short }) => {
        switch (true) {
            case !!leftIcon && short:
                return '54px';

            case short:
                return '14px';

            case leftCheckbox:
                return '54px';

            case !!leftImage:
                return '120px';

            case !!leftIcon:
                return '62px';

            default:
                return '18px';
        }
    }};
    padding-right: ${({ rightImage, rightIcon, rightCheckbox, short }) => {
        switch (true) {
            case !!rightIcon && short:
                return '54px';

            case short:
                return '14px';

            case rightCheckbox:
                return '50px';

            case !!rightImage:
                return '120px';

            case !!rightIcon:
                return '58px';

            default:
                return '18px';
        }
    }};
`;

export const StyledTitleBox = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

export const StyledBadgeBox = styled(Box)<{ $isBadgeRightCorner: boolean; $badgeMaxWith?: number }>`
    width: fit-content;

    ${({ $badgeMaxWith }) => css`
        height: 23px;
        max-width: ${$badgeMaxWith}px;
        padding: 4px 4px;
        p {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    `};
    ${({ $isBadgeRightCorner }) =>
        $isBadgeRightCorner &&
        css`
            position: absolute;
            top: -2px;
            right: -2px;
            border-radius: 0 12px 0 12px;
            padding-top: 4px;
            padding-left: 8px;
        `};
`;

export const StyledIconBox = styled.div<IStyledImageBox>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${({ left, short }) => `${left ? 'left: ' : 'right: '} ${short ? '14px' : '18px'}`};
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const StyledImageBox = styled.div<IStyledImageBox>`
    position: absolute;
    width: 110px;
    min-width: 110px;
    max-width: 110px;
    height: 100%;
    bottom: 0;
    top: ${({ borderWidth }) => `-${borderWidth ? borderWidth : 2}px`};
    ${({ left, borderWidth }) => `${left ? 'left: ' : 'right: '} -${borderWidth ? borderWidth : 2}px`};
    ${({ left }) => !left && 'margin-left: 5px'};
`;

export const StyledIndicator = styled.div<IStyledImageBox>`
    position: absolute;
    width: 100%;
    top: 50%;
    transform: translateY(-50%);
    ${({ left }) => (left ? 'left: ' : 'right: ')} 18px;
    max-width: 24px;
    height: 24px;
    box-sizing: border-box;
    border-radius: 4px;
    margin-right: 4px;
    border: 2px solid ${({ theme }) => theme['border-default']};
    background-color: ${({ theme }) => theme['surface-main']};

    display: flex;
    justify-content: center;
    align-items: center;

    &:before {
        position: absolute;
        content: '';
        width: 100%;
        height: 100%;
    }

    & svg,
    & svg > * {
        fill: transparent;
    }
`;

export const StyledInput = styled.input<IStyledOption>`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    opacity: 0;
    z-index: 100;
    cursor: pointer;

    &:checked ~ ${StyledOption} {
        ${StyledIconBox} {
            & svg,
            & svg > * {
                fill: ${({ theme, indicated }) =>
                    indicated ? theme['primary-default'] : theme['on-primary']};
            }
        }
    }

    &:checked ~ ${StyledOption} ${StyledIndicator} {
        ${({ theme, indicated }) => {
            const color = indicated ? theme['primary-default'] : theme['on-primary'];

            return css`
                border: 2px solid ${color};
                background-color: ${color};
            `;
        }};

        & svg,
        & svg > * {
            fill: ${({ theme, indicated }) =>
                indicated ? theme['on-primary'] : theme['primary-default']};
        }
    }
`;
